/**
 * Get Product List Teaser Data
 *
 * Fetches teaser information for multiple products from NBC API.
 * Used on product list page to display enhanced product cards.
 *
 * @module services/api/nbc/getProductList
 */

import { nbcClient } from './nbcClient';
import { NBC_ENDPOINTS } from './config';
import { NBCProductListResponseSchema, type NBCProductListResponse } from '@/types/schemas/nbc';

/**
 * Fetch product list teaser data from NBC API
 *
 * Returns teaser information for multiple products including
 * headlines, images, and tags. Only returns products that have
 * NBC data - missing products are silently omitted.
 *
 * @param productIds - Array of product IDs from Player API
 * @returns Product teaser data (only for products with NBC data)
 *
 * @throws {NetworkError} If request times out or network fails
 * @throws {ValidationError} If response doesn't match schema
 * @throws {PlayerApiError} For other API errors (4xx, 5xx)
 *
 * @example Basic usage
 * const result = await getProductList(['3123123', 'dummy-1', 'HBC375']);
 * console.log(`Found NBC data for ${result.products.length} products`);
 *
 * @example Handling partial results
 * const playerProductIds = ['A', 'B', 'C', 'D', 'E']; // 5 products from Player
 * const nbcResult = await getProductList(playerProductIds);
 *
 * // NBC might only have data for 3 products
 * console.log(`NBC data: ${nbcResult.products.length}/${playerProductIds.length}`);
 *
 * // Create Map for fast lookup
 * const nbcDataMap = new Map(
 *   nbcResult.products.map((p) => [p.productId, p])
 * );
 *
 * // Merge with Player data
 * const enrichedProducts = playerProducts.map((playerProduct) => {
 *   const nbcData = nbcDataMap.get(playerProduct.id);
 *   return {
 *     ...playerProduct,
 *     nbcData, // undefined if not available
 *   };
 * });
 *
 * @example Error handling
 * try {
 *   const result = await getProductList(['123', '456']);
 *   // Use NBC teaser data
 * } catch (error) {
 *   if (error instanceof NetworkError) {
 *     console.warn('NBC unavailable, using Player data only');
 *     // Display products without NBC enrichment
 *   } else {
 *     throw error;
 *   }
 * }
 *
 * @example Empty result handling
 * const result = await getProductList(['unknown1', 'unknown2']);
 *
 * if (result.products.length === 0) {
 *   console.log('No NBC data for any products - using Player data only');
 * }
 */
export async function getProductList(productIds: string[]): Promise<NBCProductListResponse> {
  const idsParam = productIds.join(',');

  return nbcClient.get<NBCProductListResponse>({
    endpoint: NBC_ENDPOINTS.PRODUCT_LIST,
    params: {
      ids: idsParam,
    },
    responseSchema: NBCProductListResponseSchema,
  });
}
